import { OPERATORS } from 'src/app/services/constants';

function normalizeUmbraMode(productMode?: any): any | null {
  if (Array.isArray(productMode)) {
    return productMode[0] ?? null;
  }

  return productMode ?? null;
}

export function isUmbraScanMode(row: any, productMode?: any): boolean {
  let selectedMode: any = normalizeUmbraMode(productMode);
  if (selectedMode == null) {
    const sm = row?.selectedModes;
    // selectedModes may be a string key (from single-select dropdown binding) or an array of mode objects
    selectedMode = Array.isArray(sm) ? (sm[0] ?? null) : (sm ?? null);
  }
  if (row?.key !== OPERATORS.UMBRA || selectedMode == null) return false;
  // captureKey can be a full key string like "Narrow ScanSAR (NSS)" or a captureMode like "Narrow ScanSAR"
  const captureKey: string =
    typeof selectedMode === 'string' ? selectedMode : (selectedMode?.captureMode ?? '');
  return captureKey.toLowerCase().includes('scansar');
}

export function getUmbraScenePrice(resolution: any, sceneSize: string): any {
  const scenePricing = resolution?.sceneSizePricing?.[sceneSize];

  if (scenePricing == null) {
    return resolution?.cost;
  }

  return scenePricing;
}

export function getUmbraScanModes(collectionMode: any[] | null | undefined): any[] {
  if (!Array.isArray(collectionMode)) {
    return [];
  }

  return collectionMode.filter(
    (mode: any) =>
      mode?.captureMode === 'ScanSAR' &&
      mode?.stripLengthMin != null &&
      mode?.stripLengthMax != null,
  );
}

export function findMatchingUmbraScanMode(
  collectionMode: any[] | null | undefined,
  distanceKm: number,
): any | null {
  return (
    getUmbraScanModes(collectionMode).find(
      (mode: any) =>
        distanceKm >= mode.stripLengthMin && distanceKm <= mode.stripLengthMax,
    ) ?? null
  );
}

export function getUmbraScanModeLabel(productMode?: any): string {
  const mode = normalizeUmbraMode(productMode);

  if (!mode?.captureMode) {
    return '';
  }

  if (mode?.stripLengthMin != null && mode?.stripLengthMax != null) {
    return `${mode.captureMode} ${mode.stripLengthMin}-${mode.stripLengthMax}km`;
  }

  return mode.captureMode;
}

export function validateSelectedUmbraScanLength(
  distanceKm: number,
  selectedMode: any,
  collectionMode: any[] | null | undefined,
): { valid: boolean; minLen: number; maxLen: number; modeLabel: string } {
  const mode = normalizeUmbraMode(selectedMode);

  if (mode?.stripLengthMin != null && mode?.stripLengthMax != null) {
    return {
      valid: distanceKm >= mode.stripLengthMin && distanceKm <= mode.stripLengthMax,
      minLen: mode.stripLengthMin,
      maxLen: mode.stripLengthMax,
      modeLabel: getUmbraScanModeLabel(mode),
    };
  }

  const scanModes = getUmbraScanModes(collectionMode);
  const minLen = scanModes.length
    ? Math.min(...scanModes.map((mode: any) => mode.stripLengthMin))
    : 0;
  const maxLen = scanModes.length
    ? Math.max(...scanModes.map((mode: any) => mode.stripLengthMax))
    : 0;

  return {
    valid: scanModes.some(
      (mode: any) => distanceKm >= mode.stripLengthMin && distanceKm <= mode.stripLengthMax,
    ),
    minLen,
    maxLen,
    modeLabel: 'any available mode',
  };
}

export function applyUmbraModeConstraints(
  row: any,
  productMode: any,
): { isUmbraScan: boolean; looks: any } {
  let mode = normalizeUmbraMode(productMode);
  // If mode is a string key (from dropdown ngValue binding), resolve to the full mode object
  // so that mode.resolution and other properties are available for constraint evaluation
  if (typeof mode === 'string' && Array.isArray(row?.collection_mode)) {
    mode = row.collection_mode.find((m: any) => m.key === mode) ?? mode;
  }
  const isUmbraScan = isUmbraScanMode(row, mode);

  row.selectedLooks = null;
  row.selectedONA = row?.ONA?.[0]?.key ?? null;
  row.selectedONAOption = row?.ONA?.[0] ?? null;
  row.selectedItem = null;
  row.selectedResolutionData = null;
  row.selectedResolution = row?.resolution?.[0] ?? null;
  row.selectedResolutionValue = row?.resolution?.[0]?.value ?? null;
  row.selectedLooksValue = null;

  let looks = row.selectedResolution?.numberOfLooks?.[0];

  if (isUmbraScan) {
    for (const item of row.ONA ?? []) {
      item.disabled = false;
    }

    let firstAllowedSet = false;
    for (const item of row.resolution ?? []) {
      const isAllowed = mode?.resolution?.includes(item.value);
      item.disabled = !isAllowed;

      if (isAllowed && !firstAllowedSet) {
        firstAllowedSet = true;
        row.selectedResolution = item;
        row.selectedResolutionValue = item.value;
        row.selectedResolutionData = item.value;
      }
    }

    row.selectedMode = getUmbraScanModeLabel(mode);
    return { isUmbraScan, looks };
  }

  if (mode?.value === '5km x 10km') {
    for (const [index, item] of (row.ONA ?? []).entries()) {
      item.disabled = index > 2;
    }
    for (const item of row.resolution ?? []) {
      item.disabled = false;
    }
    return { isUmbraScan, looks };
  }

  if (mode?.value === '10km x 10km' || mode?.value === '10km x 10km(CPHD)') {
    for (const [index, item] of (row.ONA ?? []).entries()) {
      item.disabled = index > 0;
    }

    if (!row?.resolution?.[0]?.value?.includes('looks')) {
      for (const item of row.resolution ?? []) {
        item.disabled = item.key != 3 && item.key != 4;
      }

      for (const item of row.resolution ?? []) {
        if (item.key === 3 || item.key === 4) {
          row.selectedResolution = item;
          row.selectedResolutionValue = item.value;
          break;
        }
      }
    }

    return { isUmbraScan, looks };
  }

  if (mode?.value === 'SAR Video') {
    for (const item of row.resolution ?? []) {
      if (item.key != 4) {
        item.disabled = true;
      } else {
        item.disabled = false;
        row.selectedResolution = item;
        row.selectedResolutionValue = item.value;
        looks = row.selectedResolution.numberOfLooks[4];
        for (const look of row.selectedResolution.numberOfLooks ?? []) {
          if (look === 'x5') {
            row.selectedLooks = look;
            row.numberOfLooks = look;
          }
        }
      }
    }

    return { isUmbraScan, looks };
  }

  for (const item of row.ONA ?? []) {
    item.disabled = false;
  }
  for (const item of row.resolution ?? []) {
    item.disabled = false;
  }

  return { isUmbraScan, looks };
}